import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, Radio } from 'lucide-react';

/**
 * NowPlayingCard
 *
 * Polls an Icecast status-json.xsl endpoint and pulls the current track title.
 * Then resolves album artwork via the public iTunes Search API.
 * Renders a square album art "live tile" with a LIVE badge, station title and
 * a PWA external-link button.
 */
const parseTitle = (title = '') => {
  if (!title) return { artist: '', song: '' };
  const clean = title.replace(/\s+/g, ' ').trim();
  const m = clean.match(/^(.*?)\s+-\s+(.*)$/);
  if (m) return { artist: m[1].trim(), song: m[2].trim() };
  return { artist: '', song: clean };
};

const NowPlayingCard = ({
  stationName,
  statusUrl,
  pwaUrl,
  fallbackArt,
  accent = '#F59E0B',
  testIdRoot = 'now-playing',
}) => {
  const [title, setTitle] = useState('');
  const [art, setArt] = useState(fallbackArt);
  const [parsed, setParsed] = useState({ artist: '', song: '' });
  const lastQueryRef = useRef('');

  // poll icecast status
  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const r = await fetch(statusUrl, { cache: 'no-store' });
        const json = await r.json();
        const t = json?.icestats?.source?.title || json?.icestats?.source?.yp_currently_playing || '';
        if (!cancelled && t && t !== title) setTitle(t);
      } catch {/* ignore network glitches */}
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 15000);
    return () => { cancelled = true; clearInterval(id); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusUrl]);

  // resolve album art via iTunes search
  useEffect(() => {
    const p = parseTitle(title);
    setParsed(p);
    const query = (p.artist + ' ' + p.song).trim();
    if (!query || query === lastQueryRef.current) return;
    lastQueryRef.current = query;
    let cancelled = false;
    (async () => {
      try {
        const url = `https://itunes.apple.com/search?term=${encodeURIComponent(query)}&limit=1&media=music&entity=song`;
        const r = await fetch(url);
        const json = await r.json();
        const result = json?.results?.[0];
        if (result && !cancelled) {
          const big = (result.artworkUrl100 || '').replace('100x100', '600x600');
          if (big) setArt(big);
          else setArt(fallbackArt);
        } else if (!cancelled) {
          setArt(fallbackArt);
        }
      } catch {
        if (!cancelled) setArt(fallbackArt);
      }
    })();
    return () => { cancelled = true; };
  }, [title, fallbackArt]);

  return (
    <div
      data-testid={`${testIdRoot}-card`}
      className="relative rounded-3xl overflow-hidden border border-amber/15 bg-espresso/70 backdrop-blur-sm transition-all hover:-translate-y-1 hover:border-amber/40"
    >
      {/* Square album art */}
      <div className="relative w-full aspect-square overflow-hidden">
        <img
          data-testid={`${testIdRoot}-art`}
          src={art}
          alt={parsed.song || stationName}
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.currentTarget.src = fallbackArt; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-vinyl/90 via-vinyl/10 to-transparent" />

        {/* LIVE badge */}
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-vinyl/80 backdrop-blur border border-white/10">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 live-pulse" />
          <span className="text-[10px] tracking-[0.2em] uppercase text-cream font-bold">Live</span>
        </div>
      </div>

      {/* Bottom info */}
      <div className="p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
          <div className="font-display text-base sm:text-lg text-cream leading-tight" title={stationName}>
            {stationName}
          </div>
          <a
            data-testid={`${testIdRoot}-pwa-link`}
            href={pwaUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${stationName} in the app`}
            className="self-start sm:self-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber/30 text-[10px] uppercase tracking-[0.18em] text-amber hover:text-vinyl hover:bg-amber transition-colors font-body font-bold shrink-0"
          >
            <ExternalLink className="w-3 h-3" />
            Open App
          </a>
        </div>

        <div className="mt-3 min-h-[3rem]">
          {parsed.song ? (
            <>
              <div className="text-sm sm:text-base text-cream truncate" title={parsed.song}>
                {parsed.song}
              </div>
              {parsed.artist && (
                <div className="text-xs sm:text-sm text-cream/55 font-body italic truncate" title={parsed.artist}>
                  {parsed.artist}
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2 text-cream/40 text-sm">
              <Radio className="w-4 h-4 animate-pulse" />
              <span className="font-body italic">Loading current track…</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NowPlayingCard;
